import { Controller } from '@hotwired/stimulus'

const MAX_IMAGES = 3

export default class extends Controller {
  static values = { i18n: Object }

  static targets = [
    'title', 'category', 'brand', 'model', 'description', 'condition',
    'priceMode', 'priceInput', 'priceCents', 'pricePanel', 'openPricePanel',
    'municipality', 'neighborhood', 'imageInput', 'photoSlot',
    'previewTitle', 'previewCategory', 'previewDescription', 'previewPrice',
    'previewCondition', 'previewPlace'
  ]

  connect() {
    this.selectedFiles = []
    this.objectUrls = []
    this.priceModeChanged()
    this.updatePreview()
  }

  disconnect() { this.revokeObjectUrls() }

  updatePreview() {
    const title = this.titleTarget.value.trim()
    const brandModel = [this.brandTarget.value.trim(), this.modelTarget.value.trim()].filter(Boolean).join(' ')
    const description = this.descriptionTarget.value.trim()

    this.previewTitleTarget.textContent = title || brandModel || this.i18nValue.preview_title
    this.previewTitleTarget.classList.toggle('is-placeholder', !title && !brandModel)
    this.previewDescriptionTarget.textContent = description || brandModel || this.i18nValue.preview_body
    this.previewCategoryTarget.textContent = this.selectedOptionText(this.categoryTarget) || this.i18nValue.preview_category
    this.previewConditionTarget.textContent = this.selectedOptionText(this.conditionTarget)
    const city = this.selectedOptionText(this.municipalityTarget).split(',')[0]
    this.previewPlaceTarget.textContent = [this.neighborhoodTarget.value.trim(), city].filter(Boolean).join(', ') || this.i18nValue.default_municipality
    this.updatePreviewPrice()
  }

  priceModeChanged() {
    const open = this.currentPriceMode() === 'open'
    this.pricePanelTarget.hidden = open
    this.openPricePanelTarget.hidden = !open
    this.priceInputTarget.required = !open
    if (open) this.priceCentsTarget.value = ''
    else this.syncPrice()
    this.updatePreviewPrice()
  }

  priceChanged() {
    const digits = this.priceInputTarget.value.replace(/\D/g, '')
    this.priceInputTarget.value = digits ? Number(digits).toLocaleString('pt-BR') : ''
    this.syncPrice()
    this.updatePreviewPrice()
  }

  submit() { if (this.currentPriceMode() === 'fixed') this.syncPrice() }

  chooseImages(event) {
    const index = Number(event.currentTarget.dataset.index)
    if (this.selectedFiles[index]) {
      this.selectedFiles.splice(index, 1)
      this.syncImageInput()
      this.renderPhotoSlots()
    } else {
      this.imageInputTarget.click()
    }
  }

  imagesChanged() {
    const additions = Array.from(this.imageInputTarget.files)
    const known = new Set(this.selectedFiles.map((file) => this.fileKey(file)))
    const unique = additions.filter((file) => !known.has(this.fileKey(file)))
    const available = MAX_IMAGES - this.selectedFiles.length
    unique.slice(0, available).forEach((file) => this.selectedFiles.push(file))
    if (unique.length > available) window.alert(this.i18nValue.maximum_images)
    this.syncImageInput()
    this.renderPhotoSlots()
  }

  syncPrice() {
    const digits = this.priceInputTarget.value.replace(/\D/g, '')
    this.priceCentsTarget.value = digits && Number.isSafeInteger(Number(digits) * 100) ? String(Number(digits) * 100) : ''
  }

  updatePreviewPrice() {
    if (this.currentPriceMode() === 'open') {
      this.previewPriceTarget.textContent = this.i18nValue.open_price
    } else {
      const amount = this.priceInputTarget.value.trim()
      this.previewPriceTarget.textContent = amount
        ? this.interpolate(this.i18nValue.preview_price, { AMOUNT: amount })
        : this.i18nValue.preview_price_empty
    }
  }

  currentPriceMode() { return this.priceModeTargets.find((radio) => radio.checked)?.value || 'fixed' }

  selectedOptionText(select) { return select.selectedOptions[0]?.value ? select.selectedOptions[0].textContent.trim() : '' }

  syncImageInput() {
    if (typeof DataTransfer === 'undefined') return
    const transfer = new DataTransfer()
    this.selectedFiles.forEach((file) => transfer.items.add(file))
    this.imageInputTarget.files = transfer.files
  }

  renderPhotoSlots() {
    this.revokeObjectUrls()
    this.photoSlotTargets.forEach((slot, index) => {
      const file = this.selectedFiles[index]
      slot.replaceChildren()
      if (file) {
        const url = URL.createObjectURL(file)
        this.objectUrls.push(url)
        const image = document.createElement('img')
        image.src = url
        image.alt = ''
        const remove = document.createElement('span')
        remove.className = 'request-photo-slot__remove'
        remove.textContent = this.i18nValue.remove
        slot.append(image, remove)
        slot.classList.add('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.remove_image, {
          NUMBER: index + 1,
          FILENAME: file.name
        }))
      } else {
        slot.append(this.imageIcon(), this.addLabel())
        slot.classList.remove('has-image')
        slot.setAttribute('aria-label', this.interpolate(this.i18nValue.add_image, { NUMBER: index + 1 }))
      }
    })
  }

  imageIcon() {
    const namespace = 'http://www.w3.org/2000/svg'
    const svg = document.createElementNS(namespace, 'svg')
    svg.setAttribute('width', '22')
    svg.setAttribute('height', '22')
    svg.setAttribute('viewBox', '0 0 24 24')
    svg.setAttribute('fill', 'none')
    svg.setAttribute('aria-hidden', 'true')
    svg.innerHTML = '<rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9" r="1.6"/><path d="m21 16-4.5-4.5L7 21"/>'
    return svg
  }

  addLabel() {
    const label = document.createElement('span')
    label.textContent = this.i18nValue.add
    return label
  }

  interpolate(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`__${key}__`, value),
      template
    )
  }

  fileKey(file) { return `${file.name}:${file.size}:${file.lastModified}` }

  revokeObjectUrls() {
    this.objectUrls.forEach((url) => URL.revokeObjectURL(url))
    this.objectUrls = []
  }
}
